import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import intermediaryDiary from '../../../../../api/diaries/intermediaryDiary/hooks'
import { IntermediaryDiary } from '../../../../../api/diaries/intermediaryDiary/types'
import { triggerDownload } from '../../../../../api/globalFunctions'
import { APPLICATION_SETTINGS, MODAL_TYPES } from '../../../../../common/constants'
import { setDeleteCallback } from '../../../../../Components/Modals/DeleteModal/utils'
import { PrintableColumnDef } from '../../../../../Components/TanstackTable'
import { showModal } from '../../../../../store/modal'
import { formatNumberWithTwoDecimals } from '../../../../../utils/general'
import { renderAreaUnit, renderCurrency, renderCurrencyWithUnit } from '../../../../../utils/property'
import columnWidths from './columnWidths'

export const useTableColumns = () => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const currentLang = localStorage.getItem('i18nextLng') || 'hr'

    const areaUnit = APPLICATION_SETTINGS.AREA_UNIT
    const currency = APPLICATION_SETTINGS.CURRENCY

    const w = useMemo(() => ({ ...columnWidths.hr, ...(columnWidths[currentLang] ?? {}) }), [currentLang])

    const { mutate: deleteRecord } = intermediaryDiary.DeleteDiary.useMutation()
    const { mutate: downloadPdf } = intermediaryDiary.DownloadDiaryPdf.useMutation({
        onSuccess: (data, id) => {
            const blob = new Blob([Uint8Array.from(atob(data), (c) => c.charCodeAt(0))], { type: 'application/pdf' })
            triggerDownload(id, blob, `${t('form.diary.sideBarTitle')}_${id}`)
        },
    })

    const handleDelete = useCallback(
        (id: number) => {
            const callbackId = 'delete-intermediary-diary'
            setDeleteCallback(callbackId, () => deleteRecord(id))
            dispatch(showModal({ modalType: MODAL_TYPES.DELETE_MODAL, modalProps: { callbackId } }))
        },
        [dispatch, deleteRecord]
    )

    return useMemo(
        (): PrintableColumnDef<IntermediaryDiary>[] => [
            {
                header: t('app.common.propertyId'),
                id: 'internalAppPropertyId',
                accessorKey: 'internalAppPropertyId',
                enableSorting: true,
                size: w.internalAppPropertyId,
            },
            {
                header: t('table.header.diary.sellerName'),
                id: 'sellerFullName',
                accessorFn: (row) => row.intermediaryDiarySeller?.map((el) => el.fullName).join(', ') ?? null,
                cell: ({ row }) => row.original.intermediaryDiarySeller?.map((el) => <div key={el.id}>{el.fullName}</div>),
                printValue: (row) => row.intermediaryDiarySeller?.map((el) => el.fullName).join(', ') ?? '',
                enableSorting: true,
                size: w.sellerName,
            },
            {
                header: t('app.common.pin'),
                id: 'intermediaryDiarySeller.taxNumber',
                accessorFn: (row) => row.intermediaryDiarySeller?.map((el) => el.taxNumber).join(', ') ?? null,
                cell: ({ row }) => row.original.intermediaryDiarySeller?.map((el) => <div key={el.id}>{el.taxNumber}</div>),
                printValue: (row) => row.intermediaryDiarySeller?.map((el) => el.taxNumber).join(', ') ?? '',
                enableSorting: false,
                size: w.sellerOib,
            },
            {
                header: t('table.header.diary.buyerName'),
                id: 'buyerFullName',
                accessorFn: (row) => row.intermediaryDiaryBuyer?.map((el) => el.fullName).join(', ') ?? null,
                cell: ({ row }) => row.original.intermediaryDiaryBuyer?.map((el) => <div key={el.id}>{el.fullName}</div>),
                printValue: (row) => row.intermediaryDiaryBuyer?.map((el) => el.fullName).join(', ') ?? '',
                enableSorting: true,
                size: w.buyerName,
            },
            {
                header: t('app.common.pin'),
                id: 'intermediaryDiaryBuyer.taxNumber',
                accessorFn: (row) => row.intermediaryDiaryBuyer?.map((el) => el.taxNumber).join(', ') ?? null,
                cell: ({ row }) => row.original.intermediaryDiaryBuyer?.map((el) => <div key={el.id}>{el.taxNumber}</div>),
                printValue: (row) => row.intermediaryDiaryBuyer?.map((el) => el.taxNumber).join(', ') ?? '',
                enableSorting: false,
                size: w.buyerOib,
            },
            {
                header: t('table.header.diary.propertyType'),
                id: 'propertyType',
                accessorKey: 'propertyType',
                enableSorting: true,
                size: w.propertyType,
            },
            {
                header: t('app.common.location'),
                id: 'propertyLocation',
                accessorKey: 'propertyLocation',
                enableSorting: true,
                size: w.propertyLocation,
            },
            {
                header: t('app.common.street'),
                id: 'propertyStreet',
                accessorKey: 'propertyStreet',
                enableSorting: true,
                size: w.propertyStreet,
            },
            {
                header: t('app.common.area', { areaUnit: renderAreaUnit(areaUnit) }),
                id: 'areaNett',
                enableSorting: true,
                size: w.area,
                cell: ({ row }) => {
                    const areaNett = Number(row.original.areaNett)
                    return <>{areaNett ? formatNumberWithTwoDecimals(areaNett) : null}</>
                },
                printValue: (row) => {
                    const areaNett = Number(row.areaNett)
                    return areaNett ? formatNumberWithTwoDecimals(areaNett) : ''
                },
            },
            {
                header: t('app.common.energyClass'),
                id: 'propertyEnergyClass',
                accessorKey: 'propertyEnergyClass',
                enableSorting: true,
                size: w.energyClass,
            },
            {
                header: t('table.header.diary.insert'),
                id: 'propertyRegistryInsert',
                accessorKey: 'propertyRegistryInsert',
                enableSorting: true,
                size: w.propertyRegistryInsert,
            },
            {
                header: t('table.header.diary.subInsert'),
                id: 'propertyRegistrySubinsert',
                accessorKey: 'propertyRegistrySubinsert',
                enableSorting: true,
                size: w.propertyRegistrySubinsert,
            },
            {
                header: t('table.header.diary.cadastralNo'),
                id: 'propertyCadastralParcelNo',
                accessorKey: 'propertyCadastralParcelNo',
                enableSorting: true,
                size: w.propertyCadastralParcelNo,
            },
            {
                header: t('table.header.diary.cadastralMunicipality'),
                id: 'propertyCadastralMunicipality',
                accessorKey: 'propertyCadastralMunicipality',
                enableSorting: true,
                size: w.propertyCadastralMunicipality,
            },
            {
                header: t('table.header.diary.price', { price: renderCurrency(currency) }),
                id: 'finalPrice',
                enableSorting: true,
                size: w.price,
                cell: ({ row }) => {
                    const val = row.original.finalPrice
                    return <>{val ? formatNumberWithTwoDecimals(Number(val)) : null}</>
                },
                printValue: (row) => (row.finalPrice ? formatNumberWithTwoDecimals(Number(row.finalPrice)) : ''),
            },
            {
                header: t('table.header.diary.priceM2', { priceM2: renderCurrencyWithUnit(currency, areaUnit) }),
                id: 'finalPriceM2',
                enableSorting: true,
                size: w.priceM2,
                cell: ({ row }) => {
                    const val = row.original.finalPriceM2
                    return <>{val ? formatNumberWithTwoDecimals(Number(val)) : null}</>
                },
                printValue: (row) => (row.finalPriceM2 ? formatNumberWithTwoDecimals(Number(row.finalPriceM2)) : ''),
            },
            {
                header: t('table.header.diary.contractType'),
                id: 'contractType',
                accessorKey: 'contractType',
                enableSorting: true,
                size: w.contractType,
            },
            {
                header: t('app.common.commissionSeller'),
                id: 'commissionSeller',
                enableSorting: true,
                size: w.commissionSeller,
                cell: ({ row }) => {
                    const amount = row.original.commissionSeller
                    const type = row.original.commissionSellerType
                    if (!amount) return null
                    return (
                        <>
                            {formatNumberWithTwoDecimals(Number(amount))} {type}
                        </>
                    )
                },
                printValue: (row) => (row.commissionSeller ? `${formatNumberWithTwoDecimals(Number(row.commissionSeller))} ${row.commissionSellerType}` : ''),
            },
            {
                header: t('app.common.commissionBuyer'),
                id: 'commissionBuyer',
                enableSorting: true,
                size: w.commissionBuyer,
                cell: ({ row }) => {
                    const amount = row.original.commissionBuyer
                    const type = row.original.commissionBuyerType
                    if (!amount) return null
                    return (
                        <>
                            {formatNumberWithTwoDecimals(Number(amount))} {type}
                        </>
                    )
                },
                printValue: (row) => (row.commissionBuyer ? `${formatNumberWithTwoDecimals(Number(row.commissionBuyer))} ${row.commissionBuyerType}` : ''),
            },
            {
                header: t('table.header.diary.commissionPayer'),
                id: 'commissionPayer',
                accessorKey: 'commissionPayer',
                enableSorting: false,
                size: w.commissionPayer,
            },
            {
                header: t('app.common.conclusionPlace'),
                id: 'conclusionPlace',
                accessorKey: 'conclusionPlace',
                enableSorting: true,
                size: w.conclusionPlace,
            },
            {
                header: t('app.common.conclusionDate'),
                id: 'conclusionDate',
                accessorKey: 'conclusionDate',
                enableSorting: true,
                size: w.conclusionDate,
            },
            {
                header: t('table.header.diary.bussniesStatus'),
                id: 'intermediaryDiaryStatus',
                accessorKey: 'intermediaryDiaryStatus',
                enableSorting: true,
                size: w.bussniesStatus,
            },
            {
                id: 'actions',
                enableResizing: false,
                size: w.actions,
                meta: { thDivClass: 'text-center', stopPropagation: true },
                cell: ({ row }) => {
                    const diaryId = row.original.id

                    return (
                        <ul className="list-inline hstack gap-2 mb-0 justify-content-center">
                            <li className="list-inline-item">
                                <Link
                                    to="#"
                                    title={t('button.download')}
                                    className="text-warning d-inline-block"
                                    onClick={() => downloadPdf(diaryId)}
                                >
                                    <i className="mdi mdi-download fs-16"></i>
                                </Link>
                            </li>
                            <li className="list-inline-item edit">
                                <Link
                                    title={t('button.edit')}
                                    to={`/intermediary-diary/${diaryId}/edit`}
                                    className="text-info d-inline-block edit-item-btn"
                                >
                                    <i className="mdi mdi-pencil-outline fs-16"></i>
                                </Link>
                            </li>
                            <li className="list-inline-item">
                                <Link
                                    to="#"
                                    title={t('button.delete')}
                                    className="text-danger d-inline-block remove-item-btn"
                                    onClick={() => handleDelete(diaryId)}
                                >
                                    <i className="mdi mdi-trash-can-outline fs-16"></i>
                                </Link>
                            </li>
                        </ul>
                    )
                },
            },
        ],
        [w, areaUnit, currency, downloadPdf, t, handleDelete]
    )
}
